import React, { useState, useEffect } from "react";
import { Card, Col, Row, Alert, Form, Spinner } from "react-bootstrap";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function MyPayrollsPage() {
  const [startDate, setStartDate] = useState(new Date());
  const [payrolls, setPayrolls] = useState([]);
  const [filteredPayrolls, setFilteredPayrolls] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [rolePermissions, setRolePermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // Fetch payrolls for the logged-in user
  const fetchPayrolls = async (email) => {
    try {
      const payrollQuery = await getDocs(collection(db, "payrolls"));
      const payrollData = payrollQuery.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter payrolls by user email
      const userPayrolls = payrollData.filter((payroll) => payroll.email === email);
      setPayrolls(userPayrolls);
      filterPayrollsByMonth(startDate, userPayrolls);
    } catch (error) {
      console.error("Error fetching payrolls:", error);
      setPayrolls([]);
    }
  };

  // Filter payrolls by selected month
  const filterPayrollsByMonth = (date, payrollList) => {
    const selectedMonth = date.getMonth();
    const selectedYear = date.getFullYear();

    const filteredByMonth = payrollList.filter((payroll) => {
      const payrollDate = new Date(payroll.date);
      return (
        payrollDate.getMonth() === selectedMonth &&
        payrollDate.getFullYear() === selectedYear
      );
    });

    setFilteredPayrolls(filteredByMonth);
  };

  // Fetch user data and permissions
  const fetchUserData = async (authUser) => {
    setLoading(true);

    try {
      // Get user document
      const userDocRef = doc(db, "Users", authUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRole = userData.role || "";

        // Fetch role permissions
        if (userRole === "admin") {
          setRolePermissions({ canAccessPayrolls: true });
        } else {
          const roleDocRef = doc(db, "Roles", userRole);
          const roleDoc = await getDoc(roleDocRef);

          if (roleDoc.exists()) {
            setRolePermissions(roleDoc.data().permissions);
          } else {
            setRolePermissions(null);
          }
        }

        // Fetch payrolls for the user
        await fetchPayrolls(authUser.email);
      } else {
        console.warn("User document does not exist.");
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        setCurrentUser(authUser);
        fetchUserData(authUser);
      } else {
        setCurrentUser(null);
        setAccessDenied(true);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle filtering by month
  const handleDateChange = (date) => {
    setStartDate(date);
    filterPayrollsByMonth(date, payrolls);
  };

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">You do not have permission to access this page.</p>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4 p-xxl-5">
        <h1 className="text-center mb-4">My Payroll</h1>
        <Alert variant="info" className="text-center shadow-sm">
          Use the filter to view payrolls for a specific month.
        </Alert>

        {/* Filter Section */}
        <Row className="g-4 mb-4">
          <Col lg={12}>
            <Card className="shadow-sm rounded-3">
              <Card.Header className="bg-primary text-white text-center">
                Filter Payrolls by Month
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Filter by Month</Form.Label>
                  <Form.Control
                    type="month"
                    className="shadow-sm"
                    value={`${startDate.getFullYear()}-${String(
                      startDate.getMonth() + 1
                    ).padStart(2, "0")}`}
                    onChange={(e) => handleDateChange(new Date(e.target.value))}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Payrolls Section */}
        <Row className="g-4 mt-3">
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p>Loading payrolls...</p>
            </div>
          ) : filteredPayrolls.length > 0 ? (
            filteredPayrolls.map((payroll) => (
              <Col md={6} key={payroll.id}>
                <Card className="p-3 border-primary shadow-sm rounded-3 payroll-card">
                  <Card.Body>
                    <h5 className="text-center fw-bold mb-3">{payroll.username}</h5>
                    <Row>
                      <Col sm={6}>
                        <Card className="inner-card shadow-sm text-center py-3">
                          <h6>Email</h6>
                          <p>{payroll.email}</p>
                        </Card>
                      </Col>
                      <Col sm={6}>
                        <Card className="inner-card shadow-sm text-center py-3">
                          <h6>Biweekly Salary</h6>
                          <p>${payroll.biweeklySalary}</p>
                        </Card>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={6}>
                        <Card className="inner-card shadow-sm text-center py-3">
                          <h6>Biweekly Hours</h6>
                          <p>{payroll.biweeklyHours} hours</p>
                        </Card>
                      </Col>
                      <Col sm={6}>
                        <Card className="inner-card shadow-sm text-center py-3">
                          <h6>Date</h6>
                          <p>{payroll.date}</p>
                        </Card>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={12}>
                        <Card className="inner-card shadow-sm text-center py-3">
                          <h6>Created At</h6>
                          <p>{new Date(payroll.CreatedAt).toLocaleString()}</p>
                        </Card>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <p className="text-center">No payrolls found for this month.</p>
          )}
        </Row>
      </div>
      <Footer />
    </React.Fragment>
  );
}
